import { Button, Card, Checkbox, Col, Form, InputNumber, Row, Select, Switch } from "antd";
import axios from "axios";
import { message } from "components/Messages/Messages";
import { SharedQuota, StudyProgramQuota } from "interfaces/events";
import { StudyProgram } from "interfaces/students";
import React, { useEffect, useState } from "react";

interface Props {
  sharedQuotas: SharedQuota[];
  studyProgramQuotas: StudyProgramQuota[];
  currentSharedQuota: SharedQuota | undefined;
  handleQuotas: (sq: SharedQuota[], spq: StudyProgramQuota[], csq: SharedQuota | undefined) => void;
  eventId: number;
}

const StudyProgramQuotaStep: React.FC<Props> = ({
  sharedQuotas,
  studyProgramQuotas,
  currentSharedQuota,
  handleQuotas,
  eventId,
}) => {
  const [form] = Form.useForm();
  const { Option } = Select;
  const [studyPrograms, setStudyPrograms] = useState<StudyProgram[]>([]);
  const [createNewSharedQuota, setCreateNewSharedQuota] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    if (studyPrograms.length > 0) return;
    axios.get<StudyProgram[]>(`/students/study-program/`).then(response => {
      const allPrograms = response.data.find(program => program.id === 0);
      const indok = response.data.find(program => program.id === 1);
      const others = response.data.find(program => program.id === 17);
      const restOfPrograms = response.data.filter(program => program.id !== 0 && program.id !== 1 && program.id !== 17);
      const sortedStudyPrograms = [
        ...(allPrograms ? [allPrograms] : []),
        ...(indok ? [indok] : []),
        ...restOfPrograms,
        ...(others ? [others] : []),
      ];
      setStudyPrograms(sortedStudyPrograms);
    });
  }, [studyPrograms]);

  useEffect(() => {
    if (currentSharedQuota) {
      setCreateNewSharedQuota(false);
    }
  }, [currentSharedQuota]);

  const onFinish = (values: StudyProgramQuota) => {
    const saveQuota = async () => {
      setLoading(true);
      try {
        // If a shared quota is selected, use that one
        // If not - the only way to access this is to select "create new shared quota"
        // so therefore we create a new shared quota
        const sharedQuota =
          currentSharedQuota ??
          (await axios
            .post<SharedQuota>(`/events/shared-quotas/`, {
              max_students_allowed: values.students_allowed,
              event: eventId,
            })
            .then(response => response.data));

        // If study program quota is set, update it, else create a new one
        const studyProgramQuotaResponse = await axios.post<StudyProgramQuota>(
          `/events/${eventId}/study-program-quotas/`,
          {
            ...values,
            shared_quota: sharedQuota?.id,
          }
        );

        const newStudyProgramQuota = studyProgramQuotaResponse.data;

        handleQuotas(
          currentSharedQuota ? sharedQuotas : [...sharedQuotas, sharedQuota as SharedQuota],
          [...studyProgramQuotas, newStudyProgramQuota],
          currentSharedQuota ?? (sharedQuota as SharedQuota)
        );

        form.resetFields();
        setCreateNewSharedQuota(false);
        message(
          "success",
          currentSharedQuota ? "Linjen er lagt til i kvoten" : "Kvote er opprettet og linje lagt til."
        );
        setLoading(false);
      } catch (error) {
        message(
          "error",
          currentSharedQuota ? "Noe gikk galt ved lagring av linje." : "Noe gikk galt ved opprettelse av kvote."
        );
        setLoading(false);
      }
    };

    saveQuota();
  };

  const quotaIndex = currentSharedQuota ? sharedQuotas.findIndex(q => q.id === currentSharedQuota.id) + 1 : null;

  return (
    <Card title="1. Plasser per trinn/linje">
      <Form form={form} onFinish={onFinish} layout="vertical" requiredMark={false}>
        <Row gutter={10}>
          <Col sm={12} xs={24}>
            <Form.Item
              name="study_programme"
              label="Linje"
              rules={[{ required: true, message: "Linje er ikke valgt." }]}
            >
              <Select
                showSearch
                placeholder="Velg linje..."
                optionFilterProp="children"
                filterOption={(input, option) =>
                  (option?.children?.toString().toLowerCase().indexOf(input.toLowerCase()) ?? -1) >= 0
                }
                style={{ width: "100%" }}
              >
                {studyPrograms.map(studyProgram => (
                  <Option key={studyProgram.id} value={studyProgram.id}>
                    {studyProgram.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col sm={12} xs={24}>
            <Form.Item
              name="year_levels_accepted"
              label="Årskull"
              rules={[{ required: true, message: "Minst et årskull må legges til." }]}
            >
              <Checkbox.Group style={{ width: "100%" }}>
                <Row>
                  <Col span={5}>
                    <Checkbox value={1}>1</Checkbox>
                  </Col>
                  <Col span={5}>
                    <Checkbox value={2}>2</Checkbox>
                  </Col>
                  <Col span={5}>
                    <Checkbox value={3}>3</Checkbox>
                  </Col>
                  <Col span={5}>
                    <Checkbox value={4}>4</Checkbox>
                  </Col>
                  <Col span={4}>
                    <Checkbox value={5}>5</Checkbox>
                  </Col>
                </Row>
              </Checkbox.Group>
            </Form.Item>
          </Col>
        </Row>
        <Row style={{ margin: "15px 0" }} gutter={10}>
          <Col md={12} xs={24}>
            <Form.Item
              name="students_allowed"
              label="Maks antall plasser"
              rules={[{ required: true, message: "Antall plasser må være mer enn 0." }]}
            >
              <InputNumber min={1} style={{ width: "100%" }} />
            </Form.Item>
          </Col>
          <Col md={8} xs={24}>
            <Form.Item label="Legg til linje i kvote">
              <Button
                type="primary"
                style={{ width: "100%" }}
                htmlType="submit"
                loading={loading}
                disabled={!currentSharedQuota && !createNewSharedQuota}
              >
                {createNewSharedQuota
                  ? "Legg til i ny kvote"
                  : currentSharedQuota
                  ? `Legg til i Kvote ${quotaIndex}`
                  : "Velg kvote for å legge til"}
              </Button>
            </Form.Item>
          </Col>
          <Col md={4} xs={24}>
            <Form.Item label="Ny kvote">
              <Switch
                checked={createNewSharedQuota}
                onChange={bool => {
                  setCreateNewSharedQuota(bool);
                  handleQuotas(sharedQuotas, studyProgramQuotas, undefined);
                }}
              />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Card>
  );
};

export default StudyProgramQuotaStep;
